import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { api, type Room, type CleaningNote, type BookingCounts } from '../lib/api';
import { useAuth } from '../lib/auth';
import { fmtClock } from '../lib/format';

const REFRESH_MS = 30_000;

export function HousekeeperLanding() {
  const { user, logout } = useAuth();
  const [rooms, setRooms] = useState<Room[]>([]);
  const [notes, setNotes] = useState<CleaningNote[]>([]);
  const [bookingCounts, setBookingCounts] = useState<BookingCounts | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [busyRoomId, setBusyRoomId] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<string>('—');

  const load = useCallback(async () => {
    try {
      // Rooms are the screen; notes and counts are extra context. Fetched
      // together so a failure in either does not blank the room list — that is
      // the part someone is standing in a corridor relying on.
      const [r, n, c] = await Promise.all([
        api.rooms(),
        api.cleaningNotes(false).catch(() => ({ notes: [] as CleaningNote[] })),
        api.bookingCounts().catch(() => null),
      ]);
      setRooms(r);
      setNotes(n.notes);
      setBookingCounts(c);
      setError(null);
      setLastRefresh(fmtClock(new Date()));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  const notesByRoom = useMemo(() => {
    const m = new Map<string, CleaningNote[]>();
    for (const n of notes) {
      const list = m.get(n.roomId);
      if (list) list.push(n);
      else m.set(n.roomId, [n]);
    }
    return m;
  }, [notes]);

  const countByRoom = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of bookingCounts?.counts ?? []) m.set(c.roomId, c.count);
    return m;
  }, [bookingCounts]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  async function markClean(room: Room) {
    if (room.housekeepingStatus === 'CLEAN') return;
    setBusyRoomId(room.id);
    setError(null);
    try {
      await api.setRoomHousekeeping(room.id, 'CLEAN', '');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyRoomId(null);
    }
  }

  const dirtyCount = rooms.filter(r => r.housekeepingStatus === 'DIRTY').length;
  const cleanCount = rooms.filter(r => r.housekeepingStatus === 'CLEAN').length;

  return (
    <div className="hsk-app">
      <header className="hsk-header">
        <div className="hsk-header-row">
          <div>
            <div className="hsk-brand">KATARA</div>
            <div className="hsk-tagline">Housekeeping</div>
          </div>
          <button type="button" className="hsk-logout" onClick={logout}>Sign out</button>
        </div>
        <div className="hsk-user">
          <strong>{user?.fullName}</strong>
          <span className="hsk-refresh">Refreshed {lastRefresh}</span>
        </div>
        <div className="hsk-summary">
          <span className="hsk-pill dirty">{dirtyCount} dirty</span>
          <span className="hsk-pill clean">{cleanCount} clean</span>
        </div>
      </header>

      {error && <div className="hsk-error">{error}</div>}

      <main className="hsk-list">
        {loading && rooms.length === 0 && (
          <div className="hsk-loading">Loading rooms…</div>
        )}
        {rooms.map(r => {
          const isDirty = r.housekeepingStatus === 'DIRTY';
          const isClean = r.housekeepingStatus === 'CLEAN';
          const busy = busyRoomId === r.id;
          return (
            <div key={r.id} className={`hsk-card ${isDirty ? 'dirty' : 'clean'}`}>
              <div className="hsk-card-body">
                <div className="hsk-room">
                  <span className="hsk-room-number">Room {r.number}</span>
                  {r.displayName && <span className="hsk-room-name">{r.displayName}</span>}
                  {/* Last night's booking count. Information for judging how
                      much attention a room needs, not an instruction. */}
                  {!!countByRoom.get(r.id) && (
                    <span className="hsk-bookings" title={`${countByRoom.get(r.id)} bookings on ${bookingCounts?.date}`}>
                      {countByRoom.get(r.id)} booking{countByRoom.get(r.id) === 1 ? '' : 's'}
                    </span>
                  )}
                </div>
                <div className={`hsk-status ${isDirty ? 'dirty' : 'clean'}`}>
                  {isDirty ? '⚠ Dirty' : '✓ Clean'}
                </div>
              </div>

              {/* The reason this screen exists on a phone: whoever is about to
                  walk into the room reads what happened in it first. */}
              {(notesByRoom.get(r.id) ?? []).map(n => (
                <div key={n.id} className="hsk-note">
                  <span className="hsk-note-icon">⚠</span>
                  <span className="hsk-note-body">
                    {n.body}
                    <span className="hsk-note-meta">— {n.createdBy}, {fmtClock(n.createdAt)}</span>
                  </span>
                </div>
              ))}
              {isDirty && (
                <button
                  type="button"
                  className="hsk-action"
                  onClick={() => markClean(r)}
                  disabled={busy}
                >
                  {busy ? 'Saving…' : 'MARK CLEAN'}
                </button>
              )}
              {isClean && (
                <div className="hsk-already-clean">All set</div>
              )}
            </div>
          );
        })}
        {!loading && rooms.length === 0 && (
          <div className="hsk-loading">No rooms to display.</div>
        )}
      </main>

      <footer className="hsk-footer">
        © Katara Group · Housekeeping
      </footer>
    </div>
  );
}
